import { useState, forwardRef, useEffect } from 'react';
import FormRenderer from '../FormHandler/FormRenderer';
import i18n from '../../../i18n';
import Icon from '../../../icons/Icon';

const ManyNestedForm = forwardRef(({ field, onChange, pk, inline = true, values }, ref) => {
    // Start with 0 forms
    const [forms, setForms] = useState([]);
    const [collapsedStates, setCollapsedStates] = useState({});
    // Store all form data as an array of objects: [{ field1: value1, field2: value2 }, ...]
    const [formData, setFormData] = useState([]);

    const parseFieldValue = (value) => {
        if (!value) {
            return [];
        }

        // If it's already an array, return it directly
        if (Array.isArray(value)) {
            return value;
        }

        // If it's already an object (not a string), wrap it in an array
        if (typeof value === 'object') {
            return [value];
        }

        // If it's a string, try to parse it (for backward compatibility)
        if (typeof value === 'string') {
            try {
                const parsed = JSON.parse(value);
                if (Array.isArray(parsed)) {
                    return parsed;
                }
                if (typeof parsed === 'object') {
                    // If it's a plain object, wrap it in an array so we can treat it uniformly
                    return [parsed];
                }
            } catch (e) {
                console.warn('Failed to parse field value as JSON:', e);
                return [];
            }
        }

        return [];
    };

    // Initialize forms from field.value if it exists (run when field.value changes)
    useEffect(() => {
        const parsedValue = parseFieldValue(field.value);
        if (parsedValue.length > 0) {
            setFormData(parsedValue);
            setForms(parsedValue.map((_, index) => ({ id: Date.now() + index, index })));
            setCollapsedStates(parsedValue.reduce((acc, _, index) => {
                acc[index] = false;
                return acc;
            }, {}));
        } else {
            setFormData([]);
            setForms([]);
            setCollapsedStates({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [field.value]);

    const toggleCollapseAll = () => {
        const allCollapsed = forms.every((_, index) => collapsedStates[index] === true);
        const newState = {};
        forms.forEach((_, index) => {
            newState[index] = !allCollapsed; // If all are collapsed, expand all; otherwise collapse all
        });
        setCollapsedStates(newState);
    };

    const addForm = () => {
        const newIndex = forms.length;
        setForms(prev => [...prev, { id: Date.now(), index: newIndex }]);
        setCollapsedStates(prev => ({
            ...prev,
            [newIndex]: false // Start expanded
        }));
        
        // Initialize empty object for new form
        const newFormObject = {};
        
        // Initialize all fields with empty values
        if (field.fields && Array.isArray(field.fields)) {
            field.fields.forEach(nestedField => {
                const nestedFieldName = nestedField.fieldName || nestedField.name;
                if (nestedFieldName) {
                    // Remove parent field name prefix if present
                    const cleanFieldName = nestedFieldName.startsWith(`${field.fieldName}.`)
                        ? nestedFieldName.substring(field.fieldName.length + 1)
                        : nestedFieldName;
                    newFormObject[cleanFieldName] = '';
                }
            });
        }
        
        const newData = [...formData, newFormObject];
        setFormData(newData);
        // Send updated data after state update to avoid infinite loops
        sendFormData(newData);
    };

    const removeForm = (index) => {
        const newData = formData.filter((_, i) => i !== index);
        setFormData(newData);
        // Send updated data after state update to avoid infinite loops
        sendFormData(newData);
        
        setForms(prev => {
            const newForms = prev.filter((_, i) => i !== index);
            return newForms;
        });
        
        setCollapsedStates(prev => {
            const newStates = {};
            // Reindex collapsed states
            Object.keys(prev).forEach(key => {
                const i = parseInt(key);
                if (i < index) {
                    newStates[i] = prev[i];
                } else if (i > index) {
                    newStates[i - 1] = prev[i];
                }
            });
            return newStates;
        });
    };
    
    // Helper function to send form data as nested array
    const sendFormData = (data) => {
        if (onChange) {
            onChange({
                name: field.fieldName,
                value: data
            });
        }
    };

    const localOnChange = (e, formIndex) => {
        if (onChange && formIndex !== undefined && formIndex !== null && e && e.name) {
            const singleValue = Array.isArray(e.value) ? e.value[0] : e.value;
            
            // Get the clean field name (remove parent field name prefix if present)
            let cleanFieldName = e.name;
            if (cleanFieldName.startsWith(`${field.fieldName}.`)) {
                cleanFieldName = cleanFieldName.substring(field.fieldName.length + 1);
            }
            
            // Calculate new data first
            const newData = [...formData];
            
            // Ensure array is large enough
            while (newData.length <= formIndex) {
                newData.push({});
            }
            
            // Update the specific field in the form object
            const formObject = { ...newData[formIndex] };
            formObject[cleanFieldName] = singleValue;
            newData[formIndex] = formObject;
            
            // Update form data
            setFormData(newData);
            
            // Send updated data as nested array after state update to avoid infinite loops
            sendFormData(newData);
        } else if (onChange) {
            onChange(e);
        }
    };

    const hasErrors = Boolean(field.errors && Object.keys(field.errors).length > 0);
    const formCount = forms.length;
    const allCollapsed = formCount > 0 && forms.every((_, index) => collapsedStates[index] === true);

    return (
        <div className={`many-nested-field-form w-100 my-2 ${hasErrors ? 'has-error' : ''}`} style={{width: 'auto', flexGrow: 1}}>
            <div
                className={`border rounded  mb-3 ${hasErrors ? 'border-danger' : 'border-secondary-subtle'}`}
                style={{backgroundColor: hasErrors ? '#fff5f5' : '#f8fafc'}}
            >
                <div className='d-flex align-items-center justify-content-between gap-2 flex-wrap px-3 py-2'>
                    <button
                        type="button"
                        className="btn btn-link text-decoration-none p-0 flex-grow-1 text-start"
                        style={{color: 'inherit', cursor: formCount > 0 ? 'pointer' : 'default'}}
                        onClick={formCount > 0 ? toggleCollapseAll : undefined}
                    >
                        <span className='d-flex align-items-center gap-2 flex-wrap'>
                            <span
                                className={`d-inline-flex align-items-center justify-content-center rounded-circle ${hasErrors ? 'bg-danger text-white' : 'bg-white text-primary border'}`}
                                style={{width: '2rem', height: '2rem', flexShrink: 0}}
                            >
                                <Icon name={allCollapsed ? 'ICON_CHEVRON_RIGHT' : 'ICON_CHEVRON_DOWN'} />
                            </span>
                            <span className='d-flex flex-column'>
                                <span className='fw-semibold'>{field.label}</span>
                                <small className='text-muted'>
                                    {i18n.t('Nested collection')} • {formCount} {formCount === 1 ? i18n.t('item') : i18n.t('items')}
                                </small>
                            </span>
                        </span>
                    </button>
                    <div className='d-flex align-items-center gap-2 ms-auto flex-wrap'>
                        <span className={`badge ${formCount > 0 ? 'bg-success-subtle text-success' : 'bg-light text-muted'}`}>
                            {formCount > 0 ? `${formCount} ${formCount === 1 ? i18n.t('entry') : i18n.t('entries')}` : i18n.t('No entries')}
                        </span>
                        {!field.required && (
                            <span className='badge bg-light text-muted border'>{i18n.t('Optional')}</span>
                        )}
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={addForm}
                        >
                            <Icon name="ICON_PLUS" className="me-1" />
                            {i18n.t('Add') + ' ' + field.label.toLowerCase()}
                        </button>
                    </div>
                </div>
            </div>
            {forms.map((form, index) => (
                <div key={form.id} className={`many-nested-form-item ${field.inline ? 'border-0' : 'mb-3 '}`}>
                    {!collapsedStates[index] && (
                        <div
                            className={`nested-field-form border rounded overflow-hidden ${field.inline ? 'mx-3' : 'mx-3 mt-2'}`}
                            style={{
                                backgroundColor: '#ffffff',
                                borderLeft: '4px solid #0d6efd',
                                boxShadow: '0 0.5rem 1rem rgba(15, 23, 42, 0.08)'
                            }}
                        >
                            <div className='d-flex align-items-center justify-content-between gap-2 flex-wrap px-3 py-2 border-bottom bg-light-subtle'>
                                <div className='d-flex align-items-center gap-2 flex-wrap'>
                                    <span className='badge bg-primary-subtle text-primary px-2 py-1'>
                                        {field.label} #{index + 1}
                                    </span>
                                    <small className='text-muted'>{i18n.t('Nested details')}</small>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => removeForm(index)}
                                    title={i18n.t('Remove nested form')}
                                >
                                    <Icon name="ICON_X" />
                                </button>
                            </div>

                            <div className={`d-flex p-3 ${field.inline ? 'rest-form-inline justify-content-start' : 'rest-form flex-column'}`}>
                            <FormRenderer 
                                inline={field.inline}
                                values={values}
                                fields={(() => {
                                    // Clone fields and set values from formData
                                    if (!field.fields) return field.fields;
                                    const formObject = formData[index] || {};
                                    const fieldsWithValues = {};
                                    Object.keys(field.fields).forEach(key => {
                                        const nestedField = field.fields[key];
                                        const nestedFieldName = nestedField.fieldName || nestedField.name;
                                        // Remove parent field name prefix if present
                                        const cleanFieldName = nestedFieldName.startsWith(`${field.fieldName}.`)
                                            ? nestedFieldName.substring(field.fieldName.length + 1)
                                            : nestedFieldName;

                                        fieldsWithValues[key] = {
                                            ...nestedField,
                                            value: formData?.[index]?.[cleanFieldName]
                                        };
                                      
                                        if(field.errors[index] && field.errors[index][key]) {
                                            fieldsWithValues[key].errors = field.errors[index][key];
                                        }
                                    });
                                    return fieldsWithValues;
                                })()}
                                index={index}
                                formIndex={index}
                                ref={ref} 
                                onChange={(e) => localOnChange(e, index)} 
                                pk={pk}
                            />
                        </div>
                        </div>
                    )}
                    {field.errors[index]?.length > 0 && Object.keys(field.errors[index]).map((key, parIndex) => (
                        <span className="invalid-feedback d-inline" key={parIndex}>
                            {field.errors[index][key].map((error, childIndex) => (
                                <span className="invalid-feedback d-block" key={`child_${parIndex}_${childIndex}`}>
                                    {key}: {error}
                                </span>
                            ))}
                        </span>
                    ))}

                </div>
            ))}
        </div>
    );
});

ManyNestedForm.displayName = 'ManyNestedForm';

export default ManyNestedForm;

